// 권위: docs/ARCH_DASHBOARD.md §4.6 탭 6-A "뉴스 / 공시" + docs/PRD_LeadingStockAgent.md FR-3.
// 백엔드: GET /api/v1/disclosures (dashboard-api 병렬 개발 — 스펙 가정).
//
// 화면 6-A "공시" 필터 · 응답 타입.
// corporate_actions_audit 테이블(=PendingCA 와 공유) 기반이나, 운영자 관점의 열람 전용이라
// Pending CA 의 검수 타입(PendingCAItem/PendingCADetail)과는 별도로 유지한다.
// (향후 백엔드 응답 스키마 차이·JOIN 결과 컬럼 추가 대응을 위해 독립 타입 유지.)

export type DisclosureEventType =
  | 'split'
  | 'reverse_split'
  | 'bonus_issue'
  | 'stock_dividend'
  | 'rights_offering'
  | 'share_cancellation'
  | 'pending'
  | string;

export type DisclosureReviewStatus =
  | 'pending_review'
  | 'approved'
  | 'rejected'
  | 'auto_accepted'
  | 'edited'
  | string;

export interface DisclosureListItem {
  auditId: number;
  rcptNo: string | null;
  corpCode: string | null;
  symbol: string | null;
  corpName: string | null;          // stock_master JOIN — null 가능.
  disclosureTitle: string | null;
  disclosureUrl: string | null;
  eventType: DisclosureEventType | null;
  exDate: string | null;
  adjustmentFactor: number | null;
  confidence: number | null;        // = llm_confidence
  reviewStatus: DisclosureReviewStatus;
  createdAt: string;                // ISO8601 (KST 변환은 UI 에서 수행)
}

export interface DisclosureListResponse {
  items: DisclosureListItem[];
  total: number;
  page: number;
  pageSize: number;
}

export interface DisclosureDetail {
  auditId: number;
  rcptNo: string | null;
  corpCode: string | null;
  symbol: string | null;
  corpName: string | null;
  disclosureTitle: string | null;
  disclosureUrl: string | null;
  /**
   * HTML strip 된 평문 (ADR-GOV-01 이후 파이프라인 저장 포맷).
   * 과거 수집 실패 케이스는 null/빈 문자열 가능 — 방어 처리 필수.
   * 최대 약 200,000자. scroll 가능한 <pre> 블록에 렌더.
   */
  disclosureBody: string | null;
  llmModel: string | null;
  llmConfidence: number | null;
  extractedEvent: Record<string, unknown> | null;
  reviewStatus: DisclosureReviewStatus;
  reviewer: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
}

export const ALL_DISCLOSURE_EVENT_TYPES: DisclosureEventType[] = [
  'split',
  'reverse_split',
  'bonus_issue',
  'stock_dividend',
  'rights_offering',
  'share_cancellation',
  'pending',
];

export const ALL_DISCLOSURE_REVIEW_STATUSES: DisclosureReviewStatus[] = [
  'pending_review',
  'approved',
  'rejected',
  'auto_accepted',
];

export const DISCLOSURE_PAGE_SIZES: number[] = [50, 100, 200];

/** 기본 기간: 최근 30일 (date picker 초기값). */
export function defaultDateRange(): { from: string; to: string } {
  const now = new Date();
  const to = toIsoDate(now);
  const fromD = new Date(now);
  fromD.setDate(fromD.getDate() - 30);
  const from = toIsoDate(fromD);
  return { from, to };
}

function toIsoDate(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// ── 필터 / URL 직렬화 ──────────────────────────────────
export interface DisclosureFilters {
  from: string;                               // "YYYY-MM-DD" (date picker)
  to: string;                                 // "YYYY-MM-DD"
  symbol: string;                             // 6자리 종목코드 (optional)
  q: string;                                  // 제목/본문 full-text
  eventType: DisclosureEventType | '';
  reviewStatus: DisclosureReviewStatus[];     // 복수 선택
  includeFailed: boolean;                     // STEP0 실패 idempotency 마커 포함 (기본 false — 숨김)
  page: number;                               // 0-origin
  pageSize: number;
}

export function buildDefaultFilters(): DisclosureFilters {
  const { from, to } = defaultDateRange();
  return {
    from,
    to,
    symbol: '',
    q: '',
    eventType: '',
    reviewStatus: [],
    includeFailed: false,
    page: 0,
    pageSize: 50,
  };
}

/** 필터 → URLSearchParams (기본값과 동일한 항목은 생략). */
export function disclosureFiltersToSearchParams(f: DisclosureFilters): URLSearchParams {
  const sp = new URLSearchParams();
  const def = buildDefaultFilters();
  if (f.from && f.from !== def.from) sp.set('from', f.from);
  if (f.to && f.to !== def.to) sp.set('to', f.to);
  if (f.symbol.trim()) sp.set('symbol', f.symbol.trim());
  if (f.q.trim()) sp.set('q', f.q.trim());
  if (f.eventType) sp.set('event_type', f.eventType);
  if (f.reviewStatus.length > 0) sp.set('review_status', f.reviewStatus.join(','));
  if (f.includeFailed) sp.set('include_failed', 'true');
  if (f.page > 0) sp.set('page', String(f.page));
  if (f.pageSize !== def.pageSize) sp.set('page_size', String(f.pageSize));
  return sp;
}

export function disclosureFiltersFromSearchParams(sp: URLSearchParams): DisclosureFilters {
  const def = buildDefaultFilters();
  const rs = (sp.get('review_status') ?? '')
    .split(',')
    .map((s) => s.trim())
    .filter(
      (s): s is DisclosureReviewStatus =>
        s.length > 0 && (ALL_DISCLOSURE_REVIEW_STATUSES as string[]).includes(s)
    );
  const evt = (sp.get('event_type') ?? '').trim();
  const eventType: DisclosureEventType | '' =
    evt && (ALL_DISCLOSURE_EVENT_TYPES as string[]).includes(evt) ? (evt as DisclosureEventType) : '';
  const pageSize = parseInt(sp.get('page_size') ?? '', 10);
  const page = parseInt(sp.get('page') ?? '', 10);
  return {
    from: sp.get('from') ?? def.from,
    to: sp.get('to') ?? def.to,
    symbol: (sp.get('symbol') ?? '').trim(),
    q: (sp.get('q') ?? '').trim(),
    eventType,
    reviewStatus: rs,
    includeFailed: (sp.get('include_failed') ?? '').toLowerCase() === 'true',
    page: Number.isFinite(page) && page > 0 ? page : 0,
    pageSize: DISCLOSURE_PAGE_SIZES.includes(pageSize) ? pageSize : def.pageSize,
  };
}

/** 필터 → API 쿼리 객체 (listDisclosures 호출용). */
export interface DisclosureListQuery {
  from?: string;
  to?: string;
  symbol?: string;
  q?: string;
  event_type?: string;
  review_status?: string;
  include_failed?: boolean;
  page: number;
  page_size: number;
}

export function disclosureFiltersToQuery(f: DisclosureFilters): DisclosureListQuery {
  return {
    from: f.from || undefined,
    to: f.to || undefined,
    symbol: f.symbol.trim() || undefined,
    q: f.q.trim() || undefined,
    event_type: f.eventType || undefined,
    review_status: f.reviewStatus.length > 0 ? f.reviewStatus.join(',') : undefined,
    include_failed: f.includeFailed ? true : undefined,
    page: Math.max(0, f.page),
    page_size: Math.max(1, Math.min(200, f.pageSize)),
  };
}
